import React, { ReactNode, useCallback, useContext, useEffect, useState, useMemo } from "react";
import { useLocation } from "react-router-dom";
import { QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";

import { creationEntitiesKeys } from "@/lib/api/creation-entities-keys";
import { API_URL, getQueryClientForDepartment } from "@/lib/api/client";
import { UserInfoResponseDto, UserInfoUserType } from "@/lib/api/interfaces/user-profile.interface";
import { clearSentryUserContext, setSentryUserContext } from "@/lib/monitoring/sentry";
import { useUserProfileuserinfo, userProfileKeys } from "@/lib/api/requests/user-profile.requests";
import { shouldSkipUserFetch } from "@/lib/routes/config";
import ErrorModal from "@/ui-components/ui/error-modal";
import { BlueLoadingFallback, SuspenseWrapper } from "@/ui-components/ui/suspense-wrapper";

import type { IContext, ErrorDebugContext, Error } from "./types";
import { useAuthManager } from "./hooks/useAuthManager";
import { useDepartmentManager } from "./hooks/useDepartmentManager";
import { useGlobalErrorManager } from "./hooks/useGlobalErrorManager";

// Re-export types so we don't break existing imports
export type { IContext, ErrorDebugContext, Error };

/**
 * Global App Context.
 * Provides user session, authentication state, global error handling, and department selection.
 */
export const Context = React.createContext<IContext>({
  accessToken: "",
  getAccessToken: async () => "",
});

/**
 * Context Provider Component.
 * Wraps the application to provide global state.
 */
export const ContextProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<UserInfoResponseDto | undefined>();
  
  const { auth0, accessToken, setAccessToken, isTokenReady, getAccessToken } = useAuthManager();
  const { error, setError } = useGlobalErrorManager();
  const { currentDepartment, setCurrentDepartment, availableDepartments } = useDepartmentManager(user);

  const refetchUser = useCallback(async () => {
    const deptKey = currentDepartment || "default";
    const client = getQueryClientForDepartment(deptKey);
    const token = await getAccessToken({ forceRefresh: true });
    if (!token) {
      return;
    }

    const headers: Record<string, string> = {
      Accept: "application/json",
      Authorization: `Bearer ${token}`,
    };

    if (currentDepartment) {
      headers["X-Department"] = currentDepartment;
    }

    const response = await fetch(`${API_URL}/user-profile/userinfo`, {
      method: "GET",
      headers,
    });

    if (!response.ok) {
      throw new Error(`Failed to refetch user info: ${response.status}`);
    }

    const freshUser = (await response.json()) as UserInfoResponseDto;
    setUser(freshUser);
    client.setQueryData(
      [...userProfileKeys.userinfo(), ...creationEntitiesKeys.list()],
      freshUser
    );
  }, [currentDepartment, getAccessToken]);

  const value: IContext = useMemo(
    () => ({
      accessToken,
      user,
      error,
      setAccessToken,
      setUser,
      setError,
      getAccessToken,
      currentDepartment,
      setCurrentDepartment,
      availableDepartments,
      refetchUser,
    }),
    [
      accessToken,
      user,
      error,
      setAccessToken,
      setError,
      getAccessToken,
      currentDepartment,
      setCurrentDepartment,
      availableDepartments,
      refetchUser,
    ]
  );

  useEffect(() => {
    if (!auth0.isAuthenticated) {
      clearSentryUserContext();
      return;
    }

    setSentryUserContext({
      auth0Sub: auth0.user?.sub,
      dbUserId: user?._id,
      email: auth0.user?.email,
      firstName: user?.firstName,
      lastName: user?.lastName,
      organizationId: user?.organizationId,
      subscriptionType: user?.subscriptionType,
      userType: user?.userType,
    });
  }, [auth0.isAuthenticated, auth0.user, user]);

  // Show loading while Auth0 is initializing
  if (auth0.isLoading) {
    return <BlueLoadingFallback text="Authentifizierung..." />;
  }

  // Show loading while getting access token (after Auth0 initialized)
  if (auth0.isAuthenticated && !isTokenReady) {
    return <BlueLoadingFallback text="Authentifizierung..." />;
  }

  return (
    <Context.Provider value={value}>
      <ApiProvider>
        {isTokenReady ? <BaseApiCall>{children}</BaseApiCall> : <>{children}</>}
        <ErrorModal />
      </ApiProvider>
    </Context.Provider>
  );
};

/**
 * Configures the React Query Provider based on the current department.
 */
const ApiProvider = ({ children }: { children: ReactNode }) => {
  const { currentDepartment } = useContext(Context);
  const deptKey = currentDepartment || "default";
  const client = getQueryClientForDepartment(deptKey);

  return (
    <QueryClientProvider client={client} key={deptKey}>
      {children}
      {process.env.NODE_ENV === "development" && <ReactQueryDevtools />}
    </QueryClientProvider>
  );
};

/**
 * Fetches and sets user data using Suspense.
 */
const UserDataFetcher = ({ children }: { children: ReactNode }) => {
  const { setUser, user, error: contextError } = useContext(Context);
  const { data: userData, isError, error: queryError } = useUserProfileuserinfo();

  // Set user data in context once it's loaded
  useEffect(() => {
    if (userData && setUser) {
      const hasContent = Object.keys(userData).length > 0;
      const hasRole = (userData as any)?.userRole !== undefined;
      if (hasContent && hasRole) {
        setUser(userData);
      }
    }
  }, [userData, setUser]);

  // Prevent children from rendering until user is actually in context
  // This avoids intermediate loading states (like ProtectedRoute's fallback)
  // flashing before the BaseApiCall re-renders with the user present.
  if (contextError) {
    return <>{children}</>;
  }

  if (isError && queryError) {
    throw queryError; // Let the global ErrorBoundary handle it
  }

  if (!user) {
    return null;
  }

  return <>{children}</>;
};

/**
 * Orchestrates API calls that depend on auth/user readiness.
 */
const BaseApiCall = ({ children }: { children: ReactNode }) => {
  const { accessToken, user, currentDepartment } = useContext(Context);
  const location = useLocation();

  // If no access token, don't try to fetch user data
  if (!accessToken) {
    return <>{children}</>;
  }

  // If we are on verification or creation page, skip user fetch to avoid 403 loop
  if (shouldSkipUserFetch(location.pathname)) {
    return <>{children}</>;
  }

  // If user is already loaded, check department readiness for customer users
  if (user) {
    const isCustomer = user.userType === UserInfoUserType.CUSTOMER_USER;
    if (isCustomer) {
      // Check if user has departments but they haven't been processed into context yet
      // We check user.departments directly because availableDepartments state update might lag
      const hasDepartments = Array.isArray(user.departments) && user.departments.length > 0;

      if (hasDepartments && !currentDepartment) {
        return <BlueLoadingFallback text="Abteilung wird gesetzt..." />;
      }
    }
    return <>{children}</>;
  }

  // Use Suspense to handle loading state while fetching user data
  return (
    <SuspenseWrapper type="green" text="Lädt dein Profil...">
      <UserDataFetcher>{children}</UserDataFetcher>
    </SuspenseWrapper>
  );
};
